/**
 * User-facing application preferences.
 *
 * What this owns:
 *  - theme            (light / dark / system)
 *  - density          (comfortable / compact)
 *  - animationsEnabled
 *  - language         (en / hi) — applied by i18n.ts
 *  - dateFormat / numberFormat / timezone — read by `lib/format.ts`
 *
 * Storage: localStorage only for now. The backend has `/v1/auth/me` but no
 * per-user preferences table; when one is added, point `load` / `save`
 * there and keep the localStorage cache as a primer for instant first
 * paint. The shape below is forward-compatible with a JSON column.
 */
export type ThemeMode = 'light' | 'dark' | 'system'
export type Density = 'comfortable' | 'compact'
export type Language = 'en' | 'hi'
export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD MMM YYYY'
export type NumberFormat = 'en-US' | 'en-IN' | 'de-DE'

export interface UserPreferences {
  theme: ThemeMode
  density: Density
  animationsEnabled: boolean
  language: Language
  dateFormat: DateFormat
  numberFormat: NumberFormat
  timezone: string
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'system',
  density: 'comfortable',
  animationsEnabled: true,
  language: 'en',
  dateFormat: 'DD MMM YYYY',
  numberFormat: 'en-IN',
  timezone: 'Asia/Kolkata',
}

const STORAGE_KEY = 'invitecloud.preferences.v1'

export function loadPreferences(): UserPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_PREFERENCES
    const parsed = JSON.parse(raw) as Partial<UserPreferences>
    return { ...DEFAULT_PREFERENCES, ...parsed }
  } catch {
    return DEFAULT_PREFERENCES
  }
}

export function savePreferences(prefs: UserPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    /* storage full / disabled — non-fatal */
  }
}
